/**
 * @file CreateInvoice.js
 * @version 2.16.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-11
 * @modified 2026-11-03
 *
 * Lines are the shared editable grid (OrderLinesGrid, variant "invoice"), the
 * same one Add Customer Order uses, and they price the same way: picking an
 * item resolves the customer's contract for the invoice date's period, then a
 * milk powder contract, then a spot price, then the item's own sale price — and
 * the price stays editable.
 *
 * What it replaced was an "Add Line" form over a read-only table that never
 * filled in a price at all, showed totals that disagreed with the server on a
 * tax-inclusive invoice, and deleted every line's supplier and additives when
 * an invoice was edited (the lines endpoint did not return them, and a save
 * re-inserts every line). Lines map to and from the invoice's own shape in
 * utils/lineItems.
 *
 * Editing a document already in Xero, with posted editing switched on
 * (services/editPostedSwitch): GET /api/invoices/:id/edit-context first brings
 * it up to date from Xero's copy, so it opens with Xero's own lines and
 * line-amount type; the contact and "Amounts are" are fixed (void it and raise
 * a new one instead); one approved in Xero asks why before saving, showing what
 * will change (PostedEditDialog); and if the customer had been emailed it, the
 * save offers to send them a revised copy.
 *
 * 2.7.0 (2026-10-05): "Raise a copy" of a voided invoice or credit note
 * (VoidedDocumentDialog) opens this screen filled from it — the same contact,
 * reference, lines and prices, dated today, under a new number. Nothing is
 * written until it is saved.
 *
 * 2.8.0 (2026-10-08): "Add credit" / "Add debit" on an invoice
 * (utils/invoiceAdjustment) open this screen filled from it — the same contact
 * (fixed), "Credit for INV-…" / "Debit for INV-…", dated today (or the first
 * open day), its lines to cut down to what is credited or charged, under a
 * banner. The save sends `adjustment_for`, and the server links the two and,
 * for a credit, allocates it; the answer says exactly what happened and, when
 * the allocation was not made, offers "Allocate now" (AllocateCreditNoteModal
 * pre-selected to the invoice). Replaces the old row-menu "Add Debit" pre-fill,
 * which set only the contact and reference.
 *
 * 2.8.1 (2026-10-08): every pre-fill hand-off (edit, copy, credit/debit, from
 * order) is read and cleared together on arrival and only the one used is
 * acted on, so a hand-off left behind cannot pre-fill a later visit; and an
 * Add Credit / Add Debit made while this screen is already open (navigating to
 * it changes nothing) is picked up at once (ADJUST_EVENT).
 *
 * 2.10.0 (2026-10-15, Cam — customer credit notes): saving a credit note with
 * stock-tracked lines first asks, per line, whether the goods came back
 * (goods returned — back into stock in Xero) or only the price is corrected
 * (price correction — stock not changed), starting from what the line's
 * quantity suggests against the invoice it credits (2.10.1: the quantity
 * alone — the same as the invoice → price correction, fewer → goods
 * returned; both always offered; no invoice to compare with → no
 * suggestion, every line chosen)
 * (CreditStockDialog, POST /api/invoices/credit-stock); a line with no
 * suggestion must be chosen before it saves. Till (HW) credits are not asked.
 * Editing an ALLOCATED credit says before Save that its allocation comes off
 * in Xero and goes back on (utils/creditReallocation.beforeSaveNotice), and
 * after it what was re-allocated and what is left.
 *
 * 2.11.0 (2026-10-15, Cam): a save dated in a month that should be locked in
 * Xero by now is asked about (409 PRIOR_MONTH_CONFIRM,
 * usePriorMonthConfirm); Save anyway sends confirm_prior_month.
 *
 * 2.12.0 (2026-10-23, Cam): invoicing an order whose line carries a split
 * order's tonnage says so in the header ("Includes 1.94 T from SO-D…A on …");
 * the quantity is the line's own plus it, one line, as the customer sees it.
 *
 * 2.13.0 (2026-10-28, Cam — units clear before November's invoices): each line
 * from an order carries `supplied_recorded`, so the grid says under the
 * quantity whether it is the supplied quantity or the ordered one with none
 * recorded; the grid says under each price what it is per (OrderLinesGrid
 * 2.24.0). An order with a line with no unit comes back blocked
 * (orderInvoiceService.unitBlockFor) and Save is greyed with that reason.
 *
 * 2.16.0 (2026-11-03, Cam — till sales booked to account may differ from
 * the till): a till invoice whose customer did not pay at the till opens for
 * editing (the server's edit-context `till`) with its prices INCLUDING VAT, as
 * the till charged them and Xero holds them — each line's price from
 * `till.lines` (withTillPrices), "Amounts are" Tax Inclusive and fixed, the
 * contact fixed (Change customer… moves it), and a notice saying it will no
 * longer match the till sale. The server stores it the till's way again.
 *
 * 2.15.0 (2026-11-03, Cam — "Typed lines should require user to input sales
 * and purchase account codes … (and invoices or whatever)"): a typed line (no
 * inventory item) needs its account, as Save always asked — now its Account
 * cell is outlined and Save greyed with the reason (utils/lineAccountRequired;
 * the server refuses a new or changed one too, 400 ACCOUNT_REQUIRED). An
 * invoice from an order is the order's lines — a typed line's sales account
 * is set on the order, which says so before anything is invoiced.
 *
 * 2.14.0 (2026-10-30, Cam — the Item picker's X): each "Recently ordered" row
 * can be taken off this customer's list for everyone until it is ordered
 * again, with Undo (hooks/usePreviousItemRemoval).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import './DocumentForm.css';
import './CreateInvoice.css';
import Input from './Input';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import usePriorMonthConfirm, { isPriorMonthConfirm } from '../hooks/usePriorMonthConfirm';
import ContactModal from './ContactModal';
import ContactEditModal from './ContactEditModal';
import LoadingSpinner from './LoadingSpinner';
import InventoryItemFormModal from './InventoryItemFormModal';
import OrderLinesGrid, { blankLine, lineIsEmpty, lineTotals, LineTotalsPanel } from './OrderLinesGrid';
import CustomerNotesPanel from './CustomerNotesPanel';
import { useSlideOutButton } from './SlideOutButton';
import { toISODate } from '../utils/periodHelper';
import { INVOICE_FROM_ORDER_KEY } from '../utils/invoiceFromOrder';
import PriceSuggestions from './PriceSuggestions';
import { invoiceLinesFromApi, invoiceLinesToPayload, resolveItemForVariant } from '../utils/lineItems';
import { wordsOnItemPick } from '../utils/pickedItemWords';
import useDateRepricing from '../hooks/useDateRepricing';
import DatePriceChangesDialog from './DatePriceChangesDialog';
import useXeroLock from '../hooks/useXeroLock';
import PostedEditDialog from './PostedEditDialog';
import { describeInvoiceEdit } from '../utils/postedEditChanges';
import { documentLineAccountRefusals, accountRefusalMessage } from '../utils/lineAccountRequired';
import { formatDate } from '../utils/dateFormatters';
import { formatDecimalInput } from '../utils/numberFormatters';
import { COPY_INVOICE_KEY } from '../utils/voidedDocument';
import AllocateCreditNoteModal from './AllocateCreditNoteModal';
import CreditStockDialog from './CreditStockDialog';
import { beforeSaveNotice } from '../utils/creditReallocation';
import usePreviousItemRemoval from '../hooks/usePreviousItemRemoval';
import {
  takeAdjustment, hasPendingAdjustment, adjustmentLines, adjustmentBanner, adjustmentOutcome, ADJUST_EVENT
} from '../utils/invoiceAdjustment';

// Default due date: 18th of the month following the invoice date
function defaultDueDate(invoiceDateStr) {
  if (!invoiceDateStr) return '';
  const [y, m] = invoiceDateStr.split('-').map(Number);
  if (!y || !m) return '';
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-18`;
}

const getJson = async (url) => {
  const res = await fetch(url, { credentials: 'include' });
  return res.json();
};

// On an invoice made from an order, the price is the only line field to change.
const FROM_ORDER_EDITABLE = new Set(['price']);

/**
 * A till invoice's lines at the VAT-inclusive prices the server gives
 * (edit-context `till.lines`: quantity never negative — a refund line is a
 * positive quantity at a negative price, as Xero holds it). Lines the server
 * does not name are left as they are. Pure.
 */
export function withTillPrices(lines, till) {
  if (!till || !Array.isArray(till.lines) || !till.lines.length) return lines;
  const byId = new Map(till.lines.map(l => [String(l.id), l]));
  return (lines || []).map(l => {
    const t = byId.get(String(l.invoice_line_id));
    return t ? { ...l, quantity: formatDecimalInput(t.quantity), unit_price: formatDecimalInput(t.unit_price) } : l;
  });
}

function CreateInvoice({ onNavigate, isCredit = false }) {
  const [editMode, setEditMode] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  // Fields this screen does not show but an edit must keep: the Reps tracking,
  // branding theme and supplier's reference. Sent back as they were loaded
  // (the save used to omit them, and the server nulled them).
  const [savedDocFields, setSavedDocFields] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editing a document already in Xero (GET /api/invoices/:id/edit-context):
  // { enabled, inXero, posted, needsReason, block, lineAmountTypes, primed }.
  const [editCtx, setEditCtx] = useState(null);
  // The document as it was loaded, to say what a posted edit will change.
  const [editSnapshot, setEditSnapshot] = useState(null);
  // While asking why a posted document is being changed: { changes }.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  // After a posted edit to a document the customer was sent: { to, sent_at, via }.
  const [resendOffer, setResendOffer] = useState(null);
  const [navigateAfterAlert, setNavigateAfterAlert] = useState(false);

  // Contact
  const [selectedContactId, setSelectedContactId] = useState('');
  const [contactXeroContactId, setContactXeroContactId] = useState('');

  // Header fields
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  // Xero refuses a document dated on or before its lock date; the calendar stops there.
  const { firstAllowedDate } = useXeroLock();
  // A credit note has no due date (Xero's never do) — only invoices default to terms.
  const termsFor = (dateStr) => (isCredit ? '' : defaultDueDate(dateStr));
  const [dueDate, setDueDate] = useState(() => termsFor(new Date().toISOString().split('T')[0]));
  const [reference, setReference] = useState('');
  // Document series for the number this invoice will be issued under. 'HW' keeps
  // an adjustment to a hand-made HW-D invoice in the HW-D series; null uses the
  // standard INV-D series. Set by the copy and the Add Credit / Add Debit pre-fills.
  const [numberSeries, setNumberSeries] = useState(null);
  const [lineAmountTypes, setLineAmountTypes] = useState('Exclusive');
  const [notes, setNotes] = useState('');

  // Lines, in the grid's shape.
  const [lineItems, setLineItems] = useState([]);

  // Reference data
  const [contacts, setContacts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // The same list Add Customer Order uses: the whole catalogue, each product
  // badged with its customer links for the contact. Nothing is withheld.
  const [inventoryItems, setInventoryItems] = useState([]);

  // Customer pricing for the invoice date, as the order screen loads it for the
  // delivery date. See utils/customerPricing for how one price is chosen.
  const [periods, setPeriods] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [spotPrices, setSpotPrices] = useState([]);
  const [milkPowderContracts, setMilkPowderContracts] = useState([]);
  // What the spot / milk powder prices (by date) and the contracts (by period)
  // were last loaded for. Re-pricing after a date change waits for both.
  const [pricesLoadedFor, setPricesLoadedFor] = useState(null);
  const [contractsLoadedFor, setContractsLoadedFor] = useState(undefined);
  const [previousItems, setPreviousItems] = useState([]);

  // Creating an item from the Item picker: which row asked.
  const [addItemForRow, setAddItemForRow] = useState(null);

  /*
   * Invoicing a customer order ("Create Invoice" on Search Customer Orders or
   * the order window). The order decides the customer, reference, items and
   * quantities; only the dates, notes and unit prices are editable here, and
   * the server rebuilds the lines from the order on save (orderInvoiceService).
   * { id, orderNumber, customerName, unsupplied, block }
   */
  const [fromOrder, setFromOrder] = useState(null);

  /*
   * "Add credit" / "Add debit" for an invoice (utils/invoiceAdjustment):
   * { kind, original, block } from GET /api/invoices/:id/adjustment/:kind.
   * The contact is the invoice's and cannot change.
   */
  const [adjustment, setAdjustment] = useState(null);
  // A credit saved but not allocated: { credit, invoiceId, amount, message, type }
  // — offered "Allocate now", then the Allocate window itself (open: true).
  const [allocateOffer, setAllocateOffer] = useState(null);
  // order_line_id → its suggested-prices entry, as Price Orders shows them.
  const [orderSuggestions, setOrderSuggestions] = useState({});
  const [confirmUnsupplied, setConfirmUnsupplied] = useState(false);
  // Saving a credit with stock-tracked lines: { plan, choices, opts } while
  // asking goods returned / price correction (CreditStockDialog).
  const [stockAsk, setStockAsk] = useState(null);

  // After-save state
  const [savedInvoiceXeroId, setSavedInvoiceXeroId] = useState(null);
  const [savedInvoiceSource, setSavedInvoiceSource] = useState(null);

  // Dialogs
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmReset, setConfirmReset] = useState(false);
  // "Dated in a month that should be locked" — asked when the save says so.
  const priorMonth = usePriorMonthConfirm();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  /*
   * Only a brand-new invoice opens with the contact search already waiting for
   * typing. Captured once, before the edit effect clears the flags: an edit and
   * a debit note both arrive with the contact already decided, and opening a
   * search over the top of it would be in the way.
   */
  const [autoOpenContact] = useState(() =>
    typeof window !== 'undefined'
      && !sessionStorage.getItem('editInvoiceId')
      && !sessionStorage.getItem(COPY_INVOICE_KEY)
      && !hasPendingAdjustment()
      && !sessionStorage.getItem(INVOICE_FROM_ORDER_KEY));

  useSlideOutButton([loading, editMode, savedInvoiceXeroId, savedInvoiceSource, contactXeroContactId, saving, selectedContactId]);

  // The period the invoice date falls in. Compared as YYYY-MM-DD strings, as the
  // order screen does: a Date comparison put a date on a period's first day an
  // hour outside it under BST.
  const periodName = (() => {
    const day = toISODate(invoiceDate);
    if (!day) return null;
    const p = periods.find(pp => {
      const start = toISODate(pp.start_date);
      const end = toISODate(pp.end_date);
      return (!start || start <= day) && (!end || end >= day);
    });
    return p ? p.name : null;
  })();

  const needsContact = !selectedContactId;
  const lineFieldsHint = needsContact ? 'Choose a contact first — their contracts decide the prices.' : undefined;

  /*
   * Can an invoice line keep its own unit ("None" — no weight, Cam
   * 2026-10-12)? Not until migrations/invoice_line_unit_weight.sql has run;
   * the Units cell is greyed with the reason until then.
   */
  const [unitsReady, setUnitsReady] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getJson(`${API_URL}/api/invoices/line-units`)
      .then(r => { if (!cancelled) setUnitsReady(!!r?.ready); })
      .catch(() => { if (!cancelled) setUnitsReady(false); });
    return () => { cancelled = true; };
  }, []);
  const unitsUnavailable = unitsReady === false
    ? 'An invoice line cannot keep its own unit yet — a database update has to be run first (migrations/invoice_line_unit_weight.sql)'
    : null;

  useEffect(() => {
    const initializeComponent = async () => {
      /*
       * Every hand-off this screen can be opened with, read and cleared
       * together: one visit has one purpose, and whichever is not used here
       * must not pre-fill a later visit (an Add Credit left behind when the
       * screen was opened for an edit, say). First in this order wins.
       */
      const editId = sessionStorage.getItem('editInvoiceId');
      const copyId = sessionStorage.getItem(COPY_INVOICE_KEY);
      const orderId = sessionStorage.getItem(INVOICE_FROM_ORDER_KEY);
      const adj = takeAdjustment();
      sessionStorage.removeItem('editInvoiceId');
      sessionStorage.removeItem(COPY_INVOICE_KEY);
      sessionStorage.removeItem(INVOICE_FROM_ORDER_KEY);

      await Promise.all([loadContacts(), loadAccounts()]);

      if (editId) {
        setEditMode(true);
        setEditingInvoiceId(editId);
        loadInvoiceForEdit(editId);
        return;
      }

      // A copy of a voided invoice / credit note (VoidedDocumentDialog).
      if (copyId) {
        loadInvoiceForCopy(copyId);
        return;
      }

      // "Add credit" / "Add debit" for an invoice.
      if (adj) {
        loadAdjustment(adj.invoiceId, adj.kind);
        return;
      }

      if (orderId) {
        loadFromOrder(orderId);
        return;
      }
    };

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Add Credit / Add Debit while this screen is already open (from a window
   * opened over it): navigating here changes nothing, so take the hand-off
   * now. The other kind is left for the screen the navigation is about to
   * open. Whatever was on the form is replaced — that is what was asked for.
   */
  useEffect(() => {
    const onHandoff = (e) => {
      if ((e?.detail?.kind === 'credit') !== !!isCredit) return;
      const adj = takeAdjustment();
      if (!adj) return;
      setEditMode(false);
      setEditingInvoiceId(null);
      setEditCtx(null);
      setEditSnapshot(null);
      setSavedDocFields(null);
      setFromOrder(null);
      setAllocateOffer(null);
      loadAdjustment(adj.invoiceId, adj.kind);
    };
    window.addEventListener(ADJUST_EVENT, onHandoff);
    return () => window.removeEventListener(ADJUST_EVENT, onHandoff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCredit]);

  // When selectedContactId changes (e.g. from debit pre-fill), update contact details
  useEffect(() => {
    if (selectedContactId && contacts.length > 0) {
      const contact = contacts.find(c => c.id.toString() === selectedContactId.toString());
      if (contact) setContactXeroContactId(contact.xero_contact_id || '');
    }
  }, [selectedContactId, contacts]);

  /*
   * The whole contact, for the notes panel in the footer.
   *
   * Taken from the list already loaded rather than fetched again — the search
   * carries notes and largest_vehicle_access on every row (syncService
   * searchContacts), so there is nothing left to ask for.
   */
  const selectedContact = useMemo(
    () => (selectedContactId
      ? contacts.find(c => String(c.id) === String(selectedContactId))
      : null),
    [selectedContactId, contacts]);

  // The picker's X: take a row off this customer's "Recently ordered" for
  // everyone until it is ordered again (Cam, 30 Oct 2026), with Undo.
  const { removePrevious, toast: previousItemToast } = usePreviousItemRemoval({
    side: 'customer',
    accountId: selectedContactId,
    accountName: selectedContact?.company_name || '',
    previousItems,
    setPreviousItems,
  });

  // Everything scoped to the contact: its item list, its periods, what it has
  // bought before.
  useEffect(() => {
    const scope = selectedContactId ? `?accountId=${selectedContactId}` : '';
    getJson(`${API_URL}/api/orders/inventory-items${scope}`)
      .then(data => setInventoryItems(Array.isArray(data) ? data : []))
      .catch(() => setInventoryItems([]));

    if (!selectedContactId) {
      setPeriods([]);
      setPreviousItems([]);
      return;
    }
    getJson(`${API_URL}/api/orders/periods/${selectedContactId}`)
      .then(data => setPeriods(Array.isArray(data) ? data : []))
      .catch(() => setPeriods([]));
    getJson(`${API_URL}/api/orders/customer-previous-items/${selectedContactId}`)
      .then(data => setPreviousItems(Array.isArray(data) ? data : []))
      .catch(() => setPreviousItems([]));
  }, [selectedContactId]);

  // Prices that depend on the date as well as the contact.
  useEffect(() => {
    if (!selectedContactId || !invoiceDate) {
      setSpotPrices([]);
      setMilkPowderContracts([]);
      setPricesLoadedFor(invoiceDate);
      return;
    }
    const forDate = invoiceDate;
    const dates = encodeURIComponent(invoiceDate);
    Promise.all([
      getJson(`${API_URL}/api/orders/customer-spot-prices/${selectedContactId}?dates=${dates}`)
        .then(data => setSpotPrices(Array.isArray(data) ? data : []))
        .catch(() => setSpotPrices([])),
      getJson(`${API_URL}/api/orders/customer-milk-powder-contracts/${selectedContactId}?dates=${dates}`)
        .then(data => setMilkPowderContracts(Array.isArray(data) ? data : []))
        .catch(() => setMilkPowderContracts([])),
    ]).then(() => setPricesLoadedFor(forDate));
  }, [selectedContactId, invoiceDate]);

  useEffect(() => {
    if (!selectedContactId || !periodName) { setContracts([]); setContractsLoadedFor(periodName); return; }
    const forPeriod = periodName;
    getJson(`${API_URL}/api/orders/customer-contracts/${selectedContactId}?periodNames=${encodeURIComponent(periodName)}`)
      .then(data => setContracts(Array.isArray(data) ? data : []))
      .catch(() => setContracts([]))
      .then(() => setContractsLoadedFor(forPeriod));
  }, [selectedContactId, periodName]);

  // Invoice date changed with lines already priced: offer the new date's
  // contract, spot and milk powder prices.
  const repricing = useDateRepricing({
    variant: 'invoice',
    lines: lineItems,
    setLines: setLineItems,
    date: invoiceDate,
    ready: pricesLoadedFor === invoiceDate && contractsLoadedFor === periodName,
    pricing: { contracts, spotPrices, milkPowderContracts, suppliers, periodName },
    inventoryItems,
  });

  // A new invoice gets one empty row as soon as there is a contact to price it
  // for. Never on an edit, where the invoice's own lines are about to arrive.
  useEffect(() => {
    if (!selectedContactId || editMode || fromOrder) return;
    setLineItems(prev => (prev.length === 0 ? [blankLine()] : prev));
  }, [selectedContactId, editMode, fromOrder]);

  // Returns the list as well as storing it: after creating a contact we need to
  // select it immediately, and the state set above is not readable yet.
  const loadContacts = async () => {
    try {
      const data = await getJson(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&limit=10000`);
      const list = data.contacts || [];
      setContacts(list);
      // Suppliers subset for the per-line supplier column
      setSuppliers(list.filter(c => {
        const t = (c.contact_type || '').toUpperCase();
        return t === 'SUPPLIER' || t === 'BOTH';
      }));
      return list;
    } catch (error) {
      console.error('Failed to load contacts:', error);
      return [];
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await getJson(`${API_URL}/api/inventory/sales-accounts`);
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Failed to load sales accounts:', error);
    }
  };

  const loadInvoiceForEdit = async (invoiceId) => {
    setLoading(true);
    try {
      // First: is this an edit of a document in Xero, and may it be edited?
      // Also brings the document up to date from Xero's copy, so the lines
      // loaded next are Xero's own. Not fatal — the save checks again.
      let ctx = null;
      try {
        const r = await fetch(`${API_URL}/api/invoices/${invoiceId}/edit-context`, { credentials: 'include' });
        const j = await r.json();
        if (r.ok && j.success) ctx = j;
      } catch (_) { /* opened without it; the server still decides on save */ }
      setEditCtx(ctx);

      const data = await getJson(`${API_URL}/api/invoices/${invoiceId}`);
      if (!data.success) throw new Error(data.error || 'Failed to load invoice');

      // GET /api/invoices/:id answers in camelCase. This read snake_case
      // names it never sent, so every edit opened with no contact and no dates.
      const inv = data.invoice;
      setSelectedContactId(inv.accountId != null ? String(inv.accountId) : '');
      setInvoiceDate(toISODate(inv.invoiceDate) || '');
      setDueDate(toISODate(inv.dueDate) || '');
      setReference(inv.reference || '');
      setLineAmountTypes(inv.lineAmountTypes || 'Exclusive');
      setNotes(inv.notes || '');
      setSavedInvoiceXeroId(inv.xeroInvoiceId || null);
      setSavedInvoiceSource(inv.source || 'local');
      setSavedDocFields({
        accountId: inv.accountId != null ? String(inv.accountId) : '',
        trackingRep: inv.trackingRep ?? null,
        brandingThemeId: inv.brandingThemeId,
        supplierReference: inv.supplierReference || null
      });

      // Supplier, additives and discount come back with each line, so saving
      // the edit keeps them.
      const linesData = await getJson(`${API_URL}/api/invoices/${invoiceId}/lines`);
      // A till invoice booked to account (2.16.0): its prices open including
      // VAT, as the till charged them and Xero holds them (the server's
      // ctx.till.lines — utils/tillInvoiceEdit), "Amounts are" Tax Inclusive.
      const lines = withTillPrices(invoiceLinesFromApi(linesData.lines), ctx?.till);
      if (ctx?.till && !ctx.block) setLineAmountTypes('Inclusive');
      setLineItems(lines.length ? lines : [blankLine()]);
      setEditSnapshot({
        invoiceNo: inv.invoiceNo,
        invoiceDate: toISODate(inv.invoiceDate) || '',
        dueDate: toISODate(inv.dueDate) || '',
        reference: inv.reference || '',
        notes: inv.notes || '',
        lines
      });
      if (ctx?.block) {
        setAlertDialog({ isOpen: true, type: 'warning', message: `This ${isCredit ? 'credit note' : 'invoice'} cannot be edited: ${ctx.block}` });
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to load invoice: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /*
   * A new document filled from a voided one: its contact, reference, "Amounts
   * are", notes, lines and prices. Dated today (the voided one's date may be
   * in a locked period) with the usual terms, and numbered when saved — in
   * the HW series when the voided one was. Not an edit: nothing links the two.
   */
  const loadInvoiceForCopy = async (invoiceId) => {
    setLoading(true);
    try {
      const data = await getJson(`${API_URL}/api/invoices/${invoiceId}`);
      if (!data.success) throw new Error(data.error || 'Failed to load the voided document');
      const inv = data.invoice;
      const today = new Date().toISOString().split('T')[0];
      setSelectedContactId(inv.accountId != null ? String(inv.accountId) : '');
      setInvoiceDate(today);
      setDueDate(termsFor(today));
      setReference(inv.reference || '');
      setLineAmountTypes(inv.lineAmountTypes || 'Exclusive');
      setNotes(inv.notes || '');
      if (String(inv.invoiceNo || '').startsWith('HW-')) setNumberSeries('HW');
      const linesData = await getJson(`${API_URL}/api/invoices/${invoiceId}/lines`);
      // New lines: nothing points back at the voided document's own rows.
      const lines = invoiceLinesFromApi(linesData.lines).map(l => ({ ...l, invoice_line_id: null }));
      setLineItems(lines.length ? lines : [blankLine()]);
      setAlertDialog({
        isOpen: true, type: 'info',
        message: `A copy of voided ${isCredit ? 'credit note' : 'invoice'} ${inv.invoiceNo || ''}: the same contact, lines and prices, dated today. Check it, then save — it gets a new number.`
      });
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to copy the document: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /*
   * A credit or debit for an invoice: the server's header (contact, reference,
   * date in the open period, "Amounts are", series) and the invoice's own
   * lines as new lines, for the user to cut down to what is being credited or
   * charged. Nothing is written until it is saved.
   */
  const loadAdjustment = async (invoiceId, kind) => {
    setLoading(true);
    try {
      if ((kind === 'credit') !== !!isCredit) throw new Error(`open Add ${kind === 'credit' ? 'Credit' : 'Debit'} from the invoice again`);
      const ctx = await getJson(`${API_URL}/api/invoices/${invoiceId}/adjustment/${kind}`);
      if (!ctx.success) throw new Error(ctx.error || 'Failed to load the invoice');
      const d = ctx.draft;
      setAdjustment({ kind, original: ctx.original, block: ctx.block || null });
      setSelectedContactId(String(d.account_id));
      setInvoiceDate(d.invoice_date);
      setDueDate(d.due_date || '');
      setReference(d.reference || '');
      setLineAmountTypes(d.line_amount_types || 'Exclusive');
      setNumberSeries(d.number_series || null);
      const linesData = await getJson(`${API_URL}/api/invoices/${invoiceId}/lines`);
      const lines = adjustmentLines(invoiceLinesFromApi(linesData.lines));
      setLineItems(lines.length ? lines : [blankLine()]);
      if (ctx.block) {
        setAlertDialog({ isOpen: true, type: 'warning', message: `${ctx.original?.invoice_no || 'This invoice'}: ${ctx.block}.` });
      } else if (d.moved_from_today) {
        setAlertDialog({ isOpen: true, type: 'info', message: `Today is in a period Xero has locked, so this ${kind === 'credit' ? 'credit note' : 'invoice'} is dated ${formatDate(d.invoice_date)}, the first open day.` });
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to start from the invoice: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadFromOrder = async (orderId) => {
    setLoading(true);
    try {
      const data = await getJson(`${API_URL}/api/invoices/from-order/${orderId}`);
      if (!data.success) throw new Error(data.error || 'Failed to load the order');
      setFromOrder({
        id: data.order.id,
        orderNumber: data.order.orderNumber,
        customerName: data.order.customerName,
        unsupplied: data.lines.filter(l => !l.supplied_recorded).length,
        // A split order's tonnage added to a line, said here only (2.12.0).
        tonnageNote: data.tonnageNote || null,
        block: data.block || null
      });
      setSelectedContactId(String(data.order.accountId));
      setInvoiceDate(data.invoiceDate);
      setDueDate(data.dueDate);
      setReference(data.reference || '');
      setLineAmountTypes('Exclusive');
      setNotes(data.notes || '');
      // Through the same mapping as an edit; each line keeps its order line.
      const mapped = invoiceLinesFromApi(data.lines.map(l => ({ ...l, id: l.order_line_id })));
      setLineItems(mapped.map((l, i) => ({
        ...l,
        // An order line, not a stored invoice line.
        invoice_line_id: null,
        order_line_id: data.lines[i].order_line_id,
        // An unpriced line opens empty for a price rather than at 0.00.
        unit_price: data.lines[i].unit_price > 0 ? l.unit_price : '',
        // Supplied or ordered: said under the quantity (OrderLinesGrid 2.24.0).
        supplied_recorded: !!data.lines[i].supplied_recorded
      })));
      if (data.block) setAlertDialog({ isOpen: true, message: data.block, type: 'warning' });
      getJson(`${API_URL}/api/orders/customer-orders/${orderId}/suggested-prices`)
        .then(r => setOrderSuggestions(r?.suggested_prices || {}))
        .catch(() => setOrderSuggestions({}));
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to load the order: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleContactChange = (value, list = contacts) => {
    setSelectedContactId(value);
    const contact = list.find(c => c.id.toString() === value);
    setContactXeroContactId(contact?.xero_contact_id || '');
  };

  /*
   * A credit note's stock-tracked lines: ask whether each puts the goods back
   * (goods returned) or only corrects the price (price correction). The server
   * works out the rows — which lines are tracked, the invoice line each
   * credits, what its quantity suggests. True when it asked (or
   * could not check), so the save waits for the answer.
   */
  const askCreditStock = async (realLines, opts) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/credit-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          line_items: invoiceLinesToPayload(realLines, lineAmountTypes, lineTotals),
          is_credit: true,
          transaction_type: 'CREDIT',
          ...(numberSeries ? { number_series: numberSeries } : {}),
          ...(adjustment ? { adjustment_for: { invoice_id: adjustment.original.id } } : {}),
          ...(editMode && editingInvoiceId ? { invoice_id: editingInvoiceId } : {})
        })
      });
      const plan = await res.json();
      if (!res.ok || !plan.success) throw new Error(plan.error || 'the check failed');
      if (!plan.applies || !(plan.rows || []).length) return false;
      setStockAsk({ plan, choices: {}, opts });
      return true;
    } catch (error) {
      setAlertDialog({ isOpen: true, type: 'error', message: `Could not check this credit note's stock lines (${error.message}). Nothing was saved — try again.` });
      return true;
    }
  };

  // The stock answers: kept on the lines (an edit reopens with them) and sent with the save.
  const confirmCreditStock = () => {
    const { plan, choices, opts } = stockAsk;
    const kinds = {};
    if (plan.ready !== false) {
      for (const r of plan.rows) kinds[r.index] = choices[r.index] !== undefined ? choices[r.index] : r.kind;
    }
    const real = lineItems.filter(l => !lineIsEmpty(l));
    const byId = new Map(Object.entries(kinds).map(([i, k]) => [real[Number(i)]?.id, k]));
    setLineItems(prev => prev.map(l => (byId.has(l.id) ? { ...l, credit_kind: byId.get(l.id) } : l)));
    setStockAsk(null);
    handleSave({ ...opts, stockKinds: kinds });
  };

  // Save
  const handleSave = async (opts = {}) => {
    if (!selectedContactId) {
      setAlertDialog({ isOpen: true, message: 'Please select a contact', type: 'warning' });
      return;
    }

    // A line must name something. A blank row left behind in a grid that
    // always offers one is an accident, not a line.
    const realLines = lineItems.filter(l => !lineIsEmpty(l));
    const blanks = lineItems.length - realLines.length;
    if (realLines.length === 0) {
      setAlertDialog({ isOpen: true, message: 'Add at least one line with an item or a description.', type: 'warning' });
      return;
    }
    if (blanks > 0) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: `${blanks} line${blanks > 1 ? 's have' : ' has'} neither an item nor a description. Fill them in, or remove them, then save again.`
      });
      return;
    }
    // An item brings its sales account; a typed line has nothing to bring one
    // (2.15.0 — utils/lineAccountRequired; Save is greyed with this too).
    if (accountRefusals.length > 0) {
      setAlertDialog({ isOpen: true, type: 'warning', message: accountRefusalText });
      return;
    }

    if (fromOrder) {
      const unpriced = realLines.filter(l => !(parseFloat(l.unit_price) > 0));
      if (unpriced.length > 0) {
        setAlertDialog({
          isOpen: true,
          type: 'warning',
          message: `Enter a unit price for ${unpriced.map(l => l.product_name || l.product_code).join(', ')}.`
        });
        return;
      }
      // Quantities not recorded as supplied are invoiced as ordered — say so
      // once. Recorded ones (Update Delivery, Add Supplied Qty) need no check.
      if (fromOrder.unsupplied > 0 && !opts.suppliedConfirmed) {
        setConfirmUnsupplied(true);
        return;
      }
    }

    // A credit note's stock-tracked lines: goods returned or price
    // correction, asked once per save (the server refuses a line without).
    if (isCredit && !fromOrder && !opts.stockKinds) {
      const asked = await askCreditStock(realLines, opts);
      if (asked) return;
    }

    // A document already approved in Xero: say what will change, and ask why.
    if (editMode && editCtx?.needsReason && !opts.reason) {
      setReasonPrompt({
        changes: describeInvoiceEdit(editSnapshot, { invoiceDate, dueDate, reference, notes, lines: realLines }),
        opts
      });
      return;
    }

    setSaving(true);
    try {
      const url = editMode
        ? `${API_URL}/api/invoices/${editingInvoiceId}`
        : `${API_URL}/api/invoices`;
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: parseInt(selectedContactId),
          invoice_date: invoiceDate,
          due_date: dueDate || null,
          reference: reference || null,
          is_credit: isCredit,
          transaction_type: isCredit ? 'CREDIT' : 'INVOICE',
          ...(numberSeries ? { number_series: numberSeries } : {}),
          line_amount_types: lineAmountTypes,
          notes: notes || null,
          ...(fromOrder ? { customer_order_id: fromOrder.id } : {}),
          // A credit / debit for an invoice: linked (and a credit allocated) on save.
          ...(adjustment ? { adjustment_for: { invoice_id: adjustment.original.id, kind: adjustment.kind } } : {}),
          // An edit keeps what it loaded. The rep goes back only while the
          // contact is unchanged: moved to another contact, the server gives
          // the invoice that account's rep.
          ...(editMode && savedDocFields ? {
            supplier_reference: savedDocFields.supplierReference,
            ...(savedDocFields.brandingThemeId !== undefined ? { branding_theme_id: savedDocFields.brandingThemeId } : {}),
            ...(String(selectedContactId) === savedDocFields.accountId ? { tracking_rep: savedDocFields.trackingRep } : {})
          } : {}),
          ...(opts.reason ? { edit_reason: opts.reason } : {}),
          // Dated in a month that should be locked in Xero: the person said go on.
          ...(opts.priorMonthConfirmed ? { confirm_prior_month: true } : {}),
          line_items: invoiceLinesToPayload(realLines, lineAmountTypes, lineTotals)
            .map((p, i) => (fromOrder ? { ...p, order_line_id: realLines[i].order_line_id } : p))
            // The stock choices made on this save (CreditStockDialog), by line.
            .map((p, i) => (opts.stockKinds && opts.stockKinds[i] !== undefined ? { ...p, credit_kind: opts.stockKinds[i] } : p))
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setReasonPrompt(null);
        // An allocated credit edited: what came off in Xero and what went back on.
        if (editMode && result.credit_reallocation?.message) {
          const r = result.credit_reallocation;
          const partial = (r.failed || []).length || (r.unknown || []).length;
          setNavigateAfterAlert(true);
          setAlertDialog({ isOpen: true, type: partial ? 'warning' : 'success', message: `Saved. ${r.message}` });
          return;
        }
        // Emailed to the customer before: offer them the revised copy first.
        if (editMode && result.resend && Array.isArray(result.resend.to) && result.resend.to.length) {
          setResendOffer(result.resend);
          return;
        }
        // Back to where the user started: the order list for an invoice made
        // from an order (now showing Invoiced), the invoice list otherwise.
        const leave = () => { if (onNavigate) onNavigate(fromOrder ? 'search-customer-orders' : 'search-invoices'); };
        // A credit / debit for an invoice: say exactly what happened — linked,
        // allocated or why not — and offer "Allocate now" when it was not.
        if (result.adjustment) {
          const outcome = adjustmentOutcome(result);
          if (outcome.offer) {
            setAllocateOffer({
              credit: {
                ...result.invoice,
                company_name: adjustment?.original?.company_name || selectedContact?.company_name || ''
              },
              invoiceId: outcome.offer.invoiceId,
              amount: outcome.offer.amount,
              message: outcome.message,
              type: outcome.type,
              open: false
            });
          } else {
            setAlertDialog({ isOpen: true, message: outcome.message, type: outcome.type, onClose: leave });
          }
          return;
        }
        // Saved, but not in Xero yet: say so — and when it will go — before
        // leaving, rather than the old silent navigate-away.
        if (result.xeroWarning) {
          setAlertDialog({
            isOpen: true,
            message: result.xeroWarning,
            type: result.xeroQueued ? 'info' : 'warning',
            onClose: leave
          });
        } else {
          leave();
        }
      } else if (editMode && result.code === 'EDIT_REASON_REQUIRED' && !opts.reason) {
        // The server knows it is approved in Xero even if the screen did not
        // (the edit context could not be read): ask why, then save again.
        setReasonPrompt({
          changes: describeInvoiceEdit(editSnapshot, { invoiceDate, dueDate, reference, notes, lines: realLines }),
          opts
        });
      } else if (isPriorMonthConfirm(result) && !opts.priorMonthConfirmed) {
        // Dated in a month that should be locked in Xero by now (Cam,
        // 15 Oct 2026): asked; Save anyway sends it again, Go back saves nothing.
        if (await priorMonth.ask(result)) {
          await handleSave({ ...opts, priorMonthConfirmed: true });
        } else {
          setReasonPrompt(null);
        }
      } else if (result.code === 'CREDIT_STOCK_KIND_REQUIRED' && isCredit) {
        // The server found a stock line with no choice: ask, then save again.
        setReasonPrompt(null);
        await askCreditStock(realLines, { ...opts, stockKinds: undefined });
      } else {
        throw new Error(result.error || 'Failed to save');
      }
    } catch (error) {
      setReasonPrompt(null);
      setAlertDialog({ isOpen: true, message: `Failed to save: ${error.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Send the customer the revised copy ('Revised' in the subject and on the
  // PDF; email type invoice_revised), to whoever had it last.
  const sendRevisedCopy = async () => {
    const offer = resendOffer;
    setResendOffer(null);
    try {
      const res = await fetch(`${API_URL}/api/invoices/${editingInvoiceId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to: offer.to.join(', '), revised: true })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not send it');
      setNavigateAfterAlert(true);
      setAlertDialog({ isOpen: true, type: 'success', message: `Saved, and the revised copy was sent to ${offer.to.join(', ')}.` });
    } catch (error) {
      setNavigateAfterAlert(true);
      setAlertDialog({ isOpen: true, type: 'error', message: `Saved, but the revised copy was not sent: ${error.message}. Send it from Search Invoices.` });
    }
  };

  const skipRevisedCopy = () => {
    setResendOffer(null);
    if (onNavigate) onNavigate('search-invoices');
  };

  const handleCancel = () => {
    if (onNavigate) onNavigate(fromOrder ? 'search-customer-orders' : 'search-invoices');
  };

  /**
   * Start again. A new invoice goes back to blank; an edit goes back to the
   * invoice as it is saved — "reset" on an existing record meaning "throw away
   * my changes", not "empty it".
   */
  const resetForm = () => {
    setConfirmReset(false);
    if (editMode && editingInvoiceId) {
      loadInvoiceForEdit(editingInvoiceId);
      return;
    }
    if (fromOrder) {
      loadFromOrder(fromOrder.id);
      return;
    }
    if (adjustment) {
      loadAdjustment(adjustment.original.id, adjustment.kind);
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    setSelectedContactId('');
    setContactXeroContactId('');
    setInvoiceDate(today);
    setDueDate(termsFor(today));
    setReference('');
    setLineAmountTypes('Exclusive');
    setNotes('');
    setLineItems([]);
    setAddItemForRow(null);
  };

  // Always asks first.
  const requestReset = () => setConfirmReset(true);

  const docType = isCredit ? 'Credit Note' : 'Invoice';
  // Saving this edit changes Xero's copy: the contact and "Amounts are" stay put.
  const postedMode = editMode && !!editCtx?.enabled && !!editCtx?.inXero;
  const editBlock = editMode ? (editCtx?.block || null) : null;
  const fixedInXero = `Fixed on a ${docType.toLowerCase()} already in Xero — void it and raise a new one to change it`;
  // A till invoice booked to account (2.16.0): edited with VAT-inclusive
  // prices, as Xero holds it; its customer is changed with Change customer….
  const tillMode = editMode && !!editCtx?.till;
  const tillAmountsFixed = 'A till invoice is edited with its prices including VAT, as the till charged them and Xero holds them';
  const tillContactFixed = "A till invoice's customer is changed with Change customer… (Search Invoices or the invoice window)";
  const contactFixedForAdjustment = adjustment
    ? `A ${adjustment.kind} for ${adjustment.original?.invoice_no} is to the same contact` : undefined;
  /*
   * A typed line names its account (2.15.0 — utils/lineAccountRequired). Every
   * typed line here, as this screen always asked at save (Xero will not take
   * one without); the server asks it of new and changed lines. An invoice from
   * an order is the order's lines (checked there).
   */
  const accountRefusals = fromOrder ? [] : documentLineAccountRefusals(lineItems.filter(l => !lineIsEmpty(l)));
  const accountRefusalText = accountRefusalMessage(accountRefusals, 'invoice');
  const saveBlock = fromOrder?.block || editBlock || (adjustment?.block ? `${adjustment.original?.invoice_no}: ${adjustment.block}` : null)
    || accountRefusalText || null;
  // An allocated credit: saving takes its allocation off in Xero, changes it,
  // and allocates it again (services/creditReallocateService) — said first.
  const allocatedNotice = editMode && editCtx?.allocated?.reallocate
    ? beforeSaveNotice({
      creditNo: editSnapshot?.invoiceNo,
      allocations: editCtx.allocated.allocations,
      newTotal: lineItems.filter(l => !lineIsEmpty(l)).reduce((t, l) => t + lineTotals(l, lineAmountTypes).gross, 0)
    })
    : null;

  if (loading) {
    return <div className="create-invoice"><LoadingSpinner /></div>;
  }

  return (
    <div className="create-invoice document-form">

      <div className="page-header" style={{ paddingBottom: "var(--spacing-sm)" }}>
        <div className="workflow-info">
          <span className="info">
            {fromOrder
              ? `Invoicing ${fromOrder.orderNumber} — the customer, items and quantities come from the order; set the dates, notes and prices.${fromOrder.tonnageNote ? ` ${fromOrder.tonnageNote}` : ''}`
              : adjustment ? `${adjustment.kind === 'credit' ? 'Credit' : 'Debit'} for ${adjustment.original?.invoice_no}`
              : (postedMode
                ? `${editSnapshot?.invoiceNo || docType} is in Xero — saving changes Xero's copy too.`
                : '')}
          </span>
        </div>
        <div className="header-actions">
          <button data-read-safe className="btn-round btn-danger" onClick={handleCancel}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
        </div>
      </div>

      <div className="page-scroll-content">
      <div className="invoice-form-container">

        {/* Invoice Details Section */}
        <div className="form-section">
          <h2 style={{ marginBottom: "var(--spacing-lg)" }}>{docType} Details</h2>
          {adjustment && (
            <div className={`doc-notice adjustment-notice${adjustment.block ? ' warn' : ''}`} data-testid="adjustment-banner">
              <span className="material-symbols-rounded">{adjustment.kind === 'credit' ? 'remove_circle' : 'add_card'}</span>
              {adjustment.block
                ? `${adjustment.original?.invoice_no}: ${adjustment.block}.`
                : adjustmentBanner(adjustment.kind, adjustment.original)}
            </div>
          )}
          {allocatedNotice && !editBlock && (
            <div className="doc-notice warn" data-testid="allocated-credit-notice">
              <span className="material-symbols-rounded">link_off</span>
              {allocatedNotice}
            </div>
          )}
          {tillMode && !editBlock && (
            <div className="doc-notice warn" data-testid="till-edit-notice">
              <span className="material-symbols-rounded">point_of_sale</span>
              {editCtx.till.note}
              {editCtx.till.editedHereText ? ` ${editCtx.till.editedHereText}.` : ''}
            </div>
          )}
          {postedMode && (
            <div className="doc-notice warn">
              <span className="material-symbols-rounded">cloud_sync</span>
              {editBlock
                ? `This ${docType.toLowerCase()} cannot be edited: ${editBlock}.`
                : `This ${docType.toLowerCase()} is ${editCtx.posted ? 'approved ' : ''}in Xero. Only what you change here is sent to Xero, and Xero's copy is checked afterwards — if it does not match, nothing is saved.`
                  + (editCtx.needsReason ? ' You will be asked for a reason.' : '')
                  + (editCtx.primed && editCtx.primed.ok === false ? ` (Xero's copy could not be read just now — ${editCtx.primed.error}. Saving checks it again.)` : '')}
            </div>
          )}

          <div className="form-grid invoice-details-row">
            <div className="form-group doc-contact-group">
              {/* Label and "+ New Contact" share a line above the picker, as on
                  Add Purchase Order. */}
              <div className="doc-contact-label-row">
                <label>Contact <span className='required-field'>*</span></label>
                {!fromOrder && <button
                  type="button"
                  className={`btn-full doc-new-contact${postedMode || adjustment ? ' btn-disabled' : ''}`}
                  onClick={() => setShowNewContact(true)}
                  disabled={postedMode || !!adjustment}
                  title={postedMode ? fixedInXero : (adjustment ? contactFixedForAdjustment : undefined)}
                  data-write-action
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">add</span>
                  </div>
                  <span className="btn-label">New Contact</span>
                </button>}
              </div>
              <div className="doc-contact-pick">
              <div className="doc-contact-field">
                <SearchableDropdown
                  value={selectedContactId}
                  onChange={handleContactChange}
                  autoOpen={autoOpenContact}
                  disabled={!!fromOrder || postedMode || !!adjustment || tillMode}
                  title={tillMode ? tillContactFixed : undefined}
                  options={[
                    { value: '', label: 'Select a contact...' },
                    ...contacts.map(c => ({
                      value: c.id.toString(),
                      badge: c.account_no || '',
                      label: c.company_name,
                      searchText: `${c.account_no || ''} ${c.company_name}`
                    }))
                  ]}
                  placeholder="Search by account or name..."
                />
              </div>
              {selectedContactId && (
                <button
                  type="button"
                  data-read-safe
                  className="btn-round view-contact"
                  onClick={() => setShowContactModal(true)}
                  title="View contact details"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">visibility</span>
                  </div>
                </button>
              )}
              </div>
            </div>
            <div className="form-group doc-field-date">
              <label>{docType} Date <span className='required-field'>*</span></label>
              <DateInput min={firstAllowedDate} value={invoiceDate} onChange={(e) => { repricing.noteDateChange(invoiceDate, e.target.value); setInvoiceDate(e.target.value); setDueDate(termsFor(e.target.value)); }} />
            </div>
            <div className="form-group doc-field-date">
              <label>Due Date</label>
              <DateInput value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Reference</label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. SO-12345"
                disabled={!!fromOrder}
              />
            </div>
            <div className="form-group" title={tillMode ? tillAmountsFixed : (postedMode ? fixedInXero : undefined)}>
              <label>Amounts Are</label>
              <Dropdown
                value={lineAmountTypes}
                onChange={setLineAmountTypes}
                disabled={!!fromOrder || postedMode || tillMode}
                options={[
                  { value: 'Exclusive', label: 'Tax Exclusive' },
                  { value: 'Inclusive', label: 'Tax Inclusive' },
                  { value: 'NoTax', label: 'No Tax' }
                ]}
              />
            </div>
          </div>

        </div>

        {/* Line Items Section */}
        <div className="form-section">
          <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Line Items</h2>
          {fromOrder && fromOrder.unsupplied > 0 && (
            <div className="doc-notice warn">
              <span className="material-symbols-rounded">inventory_2</span>
              {fromOrder.unsupplied === 1 ? 'One line has' : `${fromOrder.unsupplied} lines have`} no supplied
              quantity recorded, so {fromOrder.unsupplied === 1 ? 'it is' : 'they are'} invoiced at the quantity ordered.
              If less (or more) went, record it on the order first (Update Delivery / Add Supplied Qty).
            </div>
          )}
          <OrderLinesGrid
            variant="invoice"
            lines={lineItems}
            onChange={setLineItems}
            inventoryItems={inventoryItems}
            previousItems={previousItems}
            onRemovePrevious={removePrevious}
            contracts={contracts}
            spotPrices={spotPrices}
            milkPowderContracts={milkPowderContracts}
            suppliers={suppliers}
            deliveryDate={invoiceDate}
            periodName={periodName}
            amountsAre={lineAmountTypes}
            accounts={accounts}
            requireAccounts={(line) => accountRefusals.some(r => r.id === line.id)}
            onAddItem={setAddItemForRow}
            unitsUnavailable={unitsUnavailable}
            editableFields={fromOrder ? FROM_ORDER_EDITABLE : null}
            priceAside={fromOrder ? (line) => (
              <PriceSuggestions
                suggestion={orderSuggestions[line.order_line_id]}
                onApply={(price) => setLineItems(prev => prev.map(l => (l.id === line.id ? { ...l, unit_price: price } : l)))}
              />
            ) : null}
            disabled={needsContact}
            disabledHint={lineFieldsHint}
            footerRight={lineItems.length > 0 && <LineTotalsPanel lines={lineItems} amountsAre={lineAmountTypes} />}
          />

          {/* Under the lines, like Create Customer Order: notes on the left,
              reset and save on the right. */}
          <div className="doc-footer">
            <div className="doc-footer-main">
              <label htmlFor="invoice-notes">Notes</label>
              <textarea
                id="invoice-notes"
                className="doc-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes (not sent to Xero)"
                rows={2}
              />
            </div>
            {/* What we already know about them, beside what is being typed —
                the same pair as the foot of Create Customer Order. Shown even
                with no contact picked: "not recorded" is information too, and a
                panel that appears and disappears moves the buttons about. */}
            <div className="doc-footer-customer">
              <CustomerNotesPanel
                notes={selectedContact?.notes}
                largestVehicleAccess={selectedContact?.largest_vehicle_access}
              />
            </div>
            <div className="doc-footer-actions">
              <button className="btn-slideout" onClick={requestReset} disabled={saving}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">replay</span>
                </div>
                <span className="btn-label">{editMode ? 'Discard Changes' : 'Reset'}</span>
              </button>
              <button className="btn-slideout btn-slideout-primary" onClick={() => handleSave()} disabled={saving || !!saveBlock} title={saveBlock || undefined}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
                    {saving ? 'sync' : 'save'}
                  </span>
                </div>
                <span className="btn-label">{saving ? 'Saving...' : (editMode ? `Update ${docType}` : `Save ${docType}`)}</span>
              </button>
            </div>
          </div>
        </div>

      </div>
      </div>

      {/* Create an inventory item without leaving a half-built invoice. The new
          item lands on the row that asked for it, priced for this customer. */}
      <InventoryItemFormModal
        isOpen={addItemForRow !== null}
        item={null}
        onClose={() => setAddItemForRow(null)}
        onSaved={async (saved) => {
          const rowIndex = addItemForRow;
          setAddItemForRow(null);
          if (!saved) return;
          const scope = selectedContactId ? `?accountId=${selectedContactId}` : '';
          const items = await getJson(`${API_URL}/api/orders/inventory-items${scope}`).catch(() => []);
          if (Array.isArray(items)) setInventoryItems(items);
          const fresh = (Array.isArray(items) && items.find(i => i.id === saved.id)) || saved;
          const resolved = resolveItemForVariant('invoice', fresh, {
            contracts, spotPrices, milkPowderContracts, suppliers,
            deliveryDate: invoiceDate, periodName
          });
          setLineItems(prev => {
            if (rowIndex === null || !prev[rowIndex]) return prev;
            const next = [...prev];
            // The new item's sale words (utils/pickedItemWords, 2026-10-28).
            const { sales_text: _s, purchase_text: _p, product_name: _n, ...rest } = resolved;
            next[rowIndex] = { ...next[rowIndex], ...rest, ...wordsOnItemPick('invoice', next[rowIndex], fresh) };
            return next;
          });
        }}
      />

      {/* New contact, created without leaving the invoice. The list is reloaded
          on save and the new contact chosen, so the next thing to do is the
          lines — the same flow as Add Purchase Order's New Supplier. */}
      {showNewContact && (
        <ContactEditModal
          contact={{ contact_type: 'CUSTOMER' }}
          onClose={() => setShowNewContact(false)}
          onSave={async (_createdInXero, created) => {
            // Chosen from the reloaded list, so its xero_contact_id and the rest
            // arrive in the same shape as any other contact's.
            const list = await loadContacts();
            if (created?.id) handleContactChange(created.id.toString(), list);
          }}
        />
      )}

      {showContactModal && selectedContactId && (
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setShowContactModal(false)}
        />
      )}

      {priorMonth.dialog}
      <ConfirmDialog
        isOpen={confirmReset}
        title={editMode ? 'Discard changes?' : 'Reset this invoice?'}
        message={editMode
          ? 'Go back to this invoice as it is saved? Anything changed since will be lost.'
          : 'Clear the contact, dates, reference, notes and every line?'}
        confirmText={editMode ? 'Discard' : 'Reset'}
        onConfirm={resetForm}
        onCancel={() => setConfirmReset(false)}
      />

      <DatePriceChangesDialog {...repricing.dialogProps} dateLabel="invoice date" />

      <ConfirmDialog
        isOpen={confirmUnsupplied}
        title="Invoice the ordered quantities?"
        message={`${fromOrder?.unsupplied === 1 ? 'One line has' : `${fromOrder?.unsupplied} lines have`} no supplied quantity recorded, so the quantity ordered will be invoiced. Is that what went?`}
        confirmText="Yes, invoice"
        onConfirm={() => { setConfirmUnsupplied(false); handleSave({ suppliedConfirmed: true }); }}
        onCancel={() => setConfirmUnsupplied(false)}
      />

      <PostedEditDialog
        isOpen={!!reasonPrompt}
        title={`Change ${editSnapshot?.invoiceNo || `this ${docType.toLowerCase()}`}?`}
        intro={`This ${docType.toLowerCase()} is approved in Xero. Saving changes Xero's copy as well as this one`
          + (allocatedNotice ? ` — ${allocatedNotice}` : '') + ':'}
        changes={reasonPrompt?.changes}
        saving={saving}
        confirmLabel={`Update ${docType}`}
        onConfirm={(reason) => handleSave({ ...(reasonPrompt?.opts || {}), reason })}
        onCancel={() => setReasonPrompt(null)}
      />

      <CreditStockDialog
        isOpen={!!stockAsk}
        plan={stockAsk?.plan}
        choices={stockAsk?.choices || {}}
        onChoose={(index, kind) => setStockAsk(prev => (prev ? { ...prev, choices: { ...prev.choices, [index]: kind } } : prev))}
        onConfirm={confirmCreditStock}
        onCancel={() => setStockAsk(null)}
      />

      <ConfirmDialog
        isOpen={!!resendOffer}
        type="info"
        title="Send the customer a revised copy?"
        message={resendOffer
          ? `${editSnapshot?.invoiceNo || docType} was sent to ${resendOffer.to.join(', ')}`
            + `${resendOffer.sent_at ? ` on ${formatDate(resendOffer.sent_at)}` : ''}`
            + `${resendOffer.via === 'statement' ? ' with their statement' : ''}. `
            + 'Send them the revised copy now? It says "Revised" in the subject and on the PDF.'
          : ''}
        confirmText="Send revised copy"
        cancelText="Not now"
        onConfirm={sendRevisedCopy}
        onCancel={skipRevisedCopy}
      />

      {/* A credit saved for an invoice but not allocated to it: say why, and
          offer the Allocate window pre-selected to the invoice. */}
      <ConfirmDialog
        isOpen={!!allocateOffer && !allocateOffer.open}
        type={allocateOffer?.type === 'success' ? 'info' : 'warning'}
        title="Credit saved — not allocated"
        message={allocateOffer?.message || ''}
        confirmText="Allocate now"
        cancelText="Later"
        onConfirm={() => setAllocateOffer(prev => ({ ...prev, open: true }))}
        onCancel={() => { setAllocateOffer(null); if (onNavigate) onNavigate('search-invoices'); }}
      />
      {allocateOffer?.open && (
        <AllocateCreditNoteModal
          isOpen={true}
          creditNote={allocateOffer.credit}
          preselectInvoiceId={allocateOffer.invoiceId}
          preselectAmount={allocateOffer.amount || null}
          onClose={() => { setAllocateOffer(null); if (onNavigate) onNavigate('search-invoices'); }}
        />
      )}

      {previousItemToast}

      {/* Alert dialog */}
      {alertDialog.isOpen && (
        <AlertDialog
          isOpen={true}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => {
            const after = alertDialog.onClose;
            setAlertDialog({ isOpen: false, message: '', type: 'info' });
            if (after) after();
            if (navigateAfterAlert) {
              setNavigateAfterAlert(false);
              if (onNavigate) onNavigate('search-invoices');
            }
          }}
        />
      )}
    </div>
  );
}

export default CreateInvoice;
